'use client';

import {
  ImageLightbox,
  useImageViewer,
  type ViewerImage,
} from '@kit/ui/image-viewer';

interface TheaterPhotoGalleryProps {
  /** Resolved public photo URLs in render order. */
  urls: string[];
  /** Pre-translated alt text per photo (parallel to `urls`). */
  alts: string[];
}

/**
 * Click-to-zoom grid of venue photos.
 *
 * BAL-380: organization exterior/interior photos open in the canonical
 * `@kit/ui/image-viewer` lightbox on click, mirroring the dancer gallery
 * (`dancer-gallery.tsx`). The viewer indexes over the photos in render
 * order so prev/next walks this group left-to-right. This is the client
 * island the server `OrganizationMediaBlock` renders for each photo group.
 */
export function TheaterPhotoGallery({ urls, alts }: TheaterPhotoGalleryProps) {
  const viewerImages: ViewerImage[] = urls.map((url, i) => ({
    id: `${i}-${url}`,
    src: url,
    alt: alts[i],
  }));
  const viewer = useImageViewer(viewerImages);

  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        {urls.map((url, i) => (
          // eslint-disable-next-line kit-components/use-kit-primitives -- aspect-video image tile; Button's default size chrome (fixed h-9 + px-4/py-2) collapses the aspect-ratio tile and can't be dropped via variant=ghost
          <button
            key={`${i}-${url}`}
            type="button"
            onClick={() => viewer.openAt(i)}
            aria-label={alts[i]}
            className="bg-muted border-foreground/15 focus-visible:ring-ring relative block aspect-video cursor-zoom-in overflow-hidden rounded-md border focus:outline-none focus-visible:ring-2"
          >
            {/* eslint-disable-next-line @next/next/no-img-element -- resolved public URL; CDN handles optimisation for org-media */}
            <img
              src={url}
              alt=""
              aria-hidden
              className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
              loading="lazy"
            />
          </button>
        ))}
      </div>
      <ImageLightbox
        images={viewerImages}
        index={viewer.index}
        onIndexChange={viewer.goTo}
        open={viewer.isOpen}
        onOpenChange={viewer.setIsOpen}
        showDownload={false}
      />
    </>
  );
}
